// The one place the New request sheet lives, plus the ⌘⇧F shortcut. (f-01928, b-01933)
//
// Renders NO button. It used to render one in the topbar beside search, which was
// a second control for something the app already had a control for — the tray's
// ＋ Request and the Requests page's ＋ New request — and it split "file a
// request" across two glyphs with only one of them taking a screenshot.
//
// The shortcut stays, because a shortcut is not a second button: it is the same
// action reached faster. Everything else calls `startCapture()` on the store.
//
// Mounted once in the topbar, so it is alive on every route.

import { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CAPTURE_HIDE_ATTR } from '../../lib/pageCapture';
import { useRequestCaptureStore } from '../../lib/requestCaptureStore';
import { useMediaQuery } from '../../lib/useMediaQuery';
import { useCan } from '../../lib/authStore';
import { CreateRequestModal } from './CreateRequestModal';

/** ⌘⇧F on Mac, Ctrl+Shift+F elsewhere. Exported so the surfaces that offer the
 *  same action as a button can teach the shortcut in their tooltip. */
export const REQUEST_CAPTURE_SHORTCUT_LABEL =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
    ? '⌘⇧F'
    : 'Ctrl Shift F';

/** Typing in a field, an editor or a contenteditable must never be hijacked. The
 *  same guard ⌘K uses — a shortcut that eats keystrokes inside a text box is
 *  worse than no shortcut. */
function isTypingTarget(el: EventTarget | null): boolean {
  const node = el as HTMLElement | null;
  if (!node || typeof node.closest !== 'function') return false;
  return !!node.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"]');
}

export function RequestCaptureHost() {
  const can = useCan();
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const open = useRequestCaptureStore((s) => s.open);
  const capture = useRequestCaptureStore((s) => s.capture);
  const capturing = useRequestCaptureStore((s) => s.capturing);
  const startCapture = useRequestCaptureStore((s) => s.startCapture);
  const close = useRequestCaptureStore((s) => s.close);

  const mayFile = can('requests.create');

  useEffect(() => {
    if (!mayFile) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'f' && e.key !== 'F') return;
      if (!e.metaKey && !e.ctrlKey) return;
      if (!e.shiftKey || e.altKey) return;
      // Inside an editor the browser/editor binding wins.
      if (isTypingTarget(e.target)) return;
      e.preventDefault();
      void startCapture();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mayFile, startCapture]);

  // Permission-gated by omission: someone who cannot file a request gets no
  // shortcut and no sheet.
  if (!mayFile) return null;

  return (
    <>
      {/* A viewfinder, not a white flash: it says WHAT is being captured (the
          whole viewport) rather than just that something happened. Hidden from the
          capture itself, and skipped under reduced motion — where the sheet
          opening with a thumbnail in it is signal enough. */}
      <AnimatePresence>
        {capturing && !reduceMotion && (
          <motion.div
            {...{ [CAPTURE_HIDE_ATTR]: '' }}
            aria-hidden
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.12 }}
            style={{
              position: 'fixed', inset: 0, zIndex: 9999, pointerEvents: 'none',
              boxShadow: 'inset 0 0 0 3px var(--accent-primary)',
              background: 'var(--accent-primary-soft)',
            }}
          />
        )}
      </AnimatePresence>

      <CreateRequestModal open={open} onClose={close} initialCapture={capture} />
    </>
  );
}
